import DateTime, { ITimeZone } from '@dealroadshow/uikit-date-time';
import { IDictionaryTimeZone } from '@/creditflowresearch/domain/vo/IDictionary';
import { DEFAULT_TIME_ZONE, GMT0_TIME_ZONE } from '@framework/timezone/constants';
import {
  MONTH_DATE_FORMAT,
  SHORT_MONTH_NAME_DAY_FULL_YEAR,
  TIME_FORMAT_12,
  TIME_FORMAT_24H,
} from '@dealroadshow/uikit-date-time/dateFormats';

interface IFormattedUpdatedAt {
  short: string,
  full: string,
}

export const convertDictionaryTimeZoneToTimeZone = (timeZone: IDictionaryTimeZone): ITimeZone => ({
  id: timeZone.id,
  timeZone: timeZone.timeZone,
  name: timeZone.name,
  abbr: timeZone.abbreviation,
  priorityCount: timeZone.priority,
  region: timeZone.region,
  daylightSaving: timeZone.daylightSaving,
  default: timeZone.default,
});

export const resolveTimeZone = (
  getTimeZone: (id: string | null) => IDictionaryTimeZone | undefined,
  id: string,
): ITimeZone => {
  const dictTz = getTimeZone(id);

  return dictTz ? convertDictionaryTimeZoneToTimeZone(dictTz) : DEFAULT_TIME_ZONE;
};

// US/Canada users expect a 12-hour clock; everyone else - Europe, Asia, Australia,
// Africa, South America, Etc/GMT - renders 24-hour, per US-5365.
const TWELVE_HOUR_ZONES = new Set([
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Toronto',
  'America/Winnipeg',
  'America/Regina',
  'America/Edmonton',
  'America/Vancouver',
  'America/Halifax',
  'America/St_Johns',
]);

export const resolveTimeFormat = (timeZone: ITimeZone): string => (
  TWELVE_HOUR_ZONES.has(timeZone.timeZone) ? TIME_FORMAT_12 : TIME_FORMAT_24H
);

export const formatDateOrTime = (
  value: string,
  timeZone: ITimeZone,
  dateFormat = MONTH_DATE_FORMAT,
  appendZoneAbbrToTime = false,
): string => {
  const today = new DateTime(new Date(), { timeZone });
  const date = new DateTime(value, { timeZone });

  if (date.isSameDay(today)) {
    const time = date.format(resolveTimeFormat(timeZone));
    return appendZoneAbbrToTime ? `${ time } ${ timeZone.abbr }` : time;
  }

  return date.format(dateFormat);
};

export const formatUpdatedAt = (updatedAt: string, timeZone = GMT0_TIME_ZONE): IFormattedUpdatedAt => ({
  short: formatDateOrTime(updatedAt, timeZone),
  full: new DateTime(updatedAt, { timeZone }).format(SHORT_MONTH_NAME_DAY_FULL_YEAR),
});
